import React, { useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { AlbumFolderWithCreatorBrandDetails } from '../../graphql/queries/album-folder';
import OutdatedAlbumFolderModal from '../global/OutdatedAlbumFolderModal';
import { isOutdatedAlbumFolder } from '../../util/album-folder';
import { OUTDATED_ALBUM_FOLDER_MODAL } from '../global/OutdatedAlbumFolderModal/modal';
import { openModal as openModalAction } from '../../actions/modal-actions';
import { AlbumDataWithParentAndSubscription } from '../../graphql/queries/albums';
import { ALBUM_TYPE } from '../../constants/albums';
import useDismissiblePersistent from '../../hooks/useDismissiblePersistent';

const WithOutdatedAlbumFolderModalHOC = WrappedComponent => {
  const propTypes = {
    albumFolder: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    openModal: PropTypes.func.isRequired,
  };

  const WithOutdatedAlbumFolderModalClass = ({
    albumFolder,
    openModal,
    ...props
  }) => {
    const {
      visible: outdatedAlbumFolderModalVisible,
      onDismiss: onOutdatedAlbumFolderModalDismiss,
    } = useDismissiblePersistent(
      `OUTDATED_ALBUM_FOLDER_MODAL_${albumFolder?.id}`,
    );

    useEffect(() => {
      if (
        outdatedAlbumFolderModalVisible &&
        albumFolder?.id &&
        albumFolder.album_type !== ALBUM_TYPE.SHUTTERBUG &&
        isOutdatedAlbumFolder(albumFolder)
      ) {
        openModal(OUTDATED_ALBUM_FOLDER_MODAL);
      }
    }, [albumFolder, outdatedAlbumFolderModalVisible]);

    return (
      <Fragment>
        <WrappedComponent {...props} />
        <OutdatedAlbumFolderModal
          albumFolder={albumFolder}
          onContinue={onOutdatedAlbumFolderModalDismiss}
        />
      </Fragment>
    );
  };

  WithOutdatedAlbumFolderModalClass.propTypes = propTypes;

  return WithOutdatedAlbumFolderModalClass;
};

/**
 * Higher order container for the allows purchase only functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const WithOutdatedAlbumFolderModal = WrappedComponent => {
  const WithOutdatedAlbumFolder = WithOutdatedAlbumFolderModalHOC(
    WrappedComponent,
  );

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      notifyOnNetworkStatusChange: true,
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      albumFolder,
      loading,
    }),
    skip: ({ match }) => !match.params?.albumFolderId,
  });

  const withAlbumData = graphql(AlbumDataWithParentAndSubscription, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { loading, album } }) => ({
      loading,
      albumFolder: album?.album_folder,
    }),
    skip: ({ match }) => !match.params?.albumId,
  });

  const mapDispatchToProps = {
    openModal: openModalAction,
  };

  return compose(
    withAlbumFolderData,
    withAlbumData,
    connect(null, mapDispatchToProps),
  )(WithOutdatedAlbumFolder);
};

export { WithOutdatedAlbumFolderModalHOC };
export default WithOutdatedAlbumFolderModal;
